import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Box, Stack } from '@mui/material'
import BaseDialog from '../BaseDialog'
import WizardHeader from '../WizardHeader'
import UploadStep from './steps/UploadStep'
import ValidationStep from './steps/ValidationStep'
import ResultStep from './steps/ResultStep'
import { useDataApi } from '../../context/DataApiContext'

const Steps = { upload: 0, validate: 1, result: 2 }

const INITIAL_STATE = {
  step: Steps.upload,
  constructor: null,
  parsedRows: [],
  loading: false,
  result: null,
  error: null,
}

export default function CalendarImport({ open, onClose, onCreated }) {
  const { t } = useTranslation()
  const { dataProvider } = useDataApi()
  const [state, setState] = useState(INITIAL_STATE)

  const reset = () => setState(INITIAL_STATE)

  const handleClose = () => {
    reset()
    onClose()
  }

  const handleUploadConfirm = ({ constructor, rows }) => {
    setState((s) => ({
      ...s,
      step: Steps.validate,
      constructor,
      parsedRows: rows,
    }))
  }

  const handleValidationConfirm = async (confirmRows) => {
    setState((s) => ({
      ...s,
      step: Steps.result,
      loading: true,
      error: null,
      result: null,
    }))
    try {
      const result = await dataProvider.calendar.confirmImport(confirmRows)
      setState((s) => ({ ...s, loading: false, result }))
      onCreated()
    } catch {
      setState((s) => ({ ...s, loading: false, error: true }))
    }
  }

  const handleBack = () => {
    setState((s) => ({ ...s, step: Steps.upload }))
  }

  const handleStepClick = (step) => {
    if (step === Steps.upload) {
      setState((s) => ({
        ...s,
        step: Steps.upload,
        parsedRows: [],
        constructor: null,
      }))
    } else if (step === Steps.validate && state.step > Steps.validate) {
      setState((s) => ({ ...s, step: Steps.validate }))
    }
  }

  return (
    <BaseDialog
      open={open}
      onClose={handleClose}
      title={t('calendar-import.dialog-title')}
      width="lg"
      hideActions
    >
      <Stack direction="column" spacing={3} sx={{ mb: 1 }}>
        <WizardHeader
          numberOfSteps={3}
          selectedStep={state.step}
          onStepClick={handleStepClick}
        />
        <Box sx={{ px: 1, pb: 2 }}>
          {state.step === Steps.upload && (
            <UploadStep onConfirm={handleUploadConfirm} />
          )}
          {state.step === Steps.validate && (
            <ValidationStep
              constructorId={state.constructor ? state.constructor.id : null}
              initialRows={state.parsedRows}
              onConfirm={handleValidationConfirm}
              onBack={handleBack}
            />
          )}
          {state.step === Steps.result && (
            <ResultStep
              loading={state.loading}
              result={state.result}
              error={state.error}
              onClose={handleClose}
            />
          )}
        </Box>
      </Stack>
    </BaseDialog>
  )
}
